import React, {useEffect, useState} from 'react';
import VeggieInfoPopup from './VeggieInfoPopup';
import LeafLoader from '../components/reusableComponents/LeafLoader';
import fallbackImage from '../images/fallback/plantFallbackImage.png';
import '../css/VeggieSelectorStyling.css';
import { supabase } from './lib/supabaseClient';

const VeggieSelector = ({ totalSpace, gardenInformation, veggieFilter = null, onAddPlants }) => {
    const [selectedCategory, setSelectedCategory] = useState('Vegetable');
    const [searchTerm, setSearchTerm] = useState('');
    const [plantCounts, setPlantCounts] = useState({});
    const [infoPopup, setInfoPopup] = useState(null);

    //db plants and useEffect
    const [veggies, setVeggies] = useState([]);
    const [loading, setLoading] = useState(true);

    // Track which images have loaded
    const [loadedImages, setLoadedImages] = useState(new Set());
    // Track which images have failed and should use fallback
    const [failedImages, setFailedImages] = useState(new Set());

    useEffect(() => {
        const fetchVeggies = async () => {
            console.log("Fetching plant data");
            const { data, error } = await supabase
                .from('plant')
                .select('*')
                .order('plant_name', { ascending: true });

            if (error) {
                console.error('Error fetching plants:', error);
            } else {
                setVeggies(data);
                console.log(data);
            }

            setLoading(false);
        };

        fetchVeggies();
    }, []);

    // Handle image load success
    const handleImageLoad = (imageName) => {
        setLoadedImages(prev => new Set([...prev, imageName]));
    };

    // Handle image error - switch to fallback
    const handleImageError = (imageName) => {
        console.error(`Failed to load image for ${imageName}, using fallback`);
        setFailedImages(prev => new Set([...prev, imageName]));
        setLoadedImages(prev => new Set([...prev, imageName]));
    };

    const groupedVeggies = veggies.reduce((acc, veggie) => {
        if (!acc[veggie.plant_type]) acc[veggie.plant_type] = [];
        acc[veggie.plant_type].push(veggie);
        return acc;
    }, {});

    const categories = Object.keys(groupedVeggies);

    const handleCountChange = (name, delta) => {
        setPlantCounts((prev) => ({
            ...prev,
            [name]: Math.max(0, (prev[name] || 0) + delta),
        }));
    };

    const handleAddingPlants = () => {
        const selectedPlants = Object.entries(plantCounts)
            .filter(([_, count]) => count > 0)
            .map(([name, count]) => {
                const veggie = veggies.find(v => v.plant_name === name);
                return {
                    id: veggie?.id,
                    name,
                    count,
                    plantingWidth: veggie?.planting_width,
                    plantingHeight: veggie?.planting_height,
                    veggieImage: veggie?.plant_image_url,
                    waterNeeds: veggie?.water_needs,
                    waterFrequency: veggie?.water_frequency,
                    waterPerApplication: veggie?.water_per_application,
                    droughtTolerance: veggie?.drought_tolerance,
                    wateringSchedule: veggie?.watering_schedule,
                };
            });

        if (onAddPlants) {
            onAddPlants(selectedPlants);
        }
    }

    const totalPlants = Object.values(plantCounts).reduce((sum, count) => sum + count, 0);

    const displayedVeggies = searchTerm
        ? veggies.filter((v) => v.plant_name.toLowerCase().includes(searchTerm.toLowerCase()))
        : veggieFilter
            ? veggies.filter((v) => veggieFilter.includes(v.plant_name))
            : groupedVeggies[selectedCategory] || [];

    // Show loading state for initial data fetch
    if (loading) {
        return (
            <div className="veggie-selector-container">
                <div className="loading-container" style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '60px 20px',
                    minHeight: '300px'
                }}>
                    <LeafLoader
                        count={7}
                        size={24}
                        speed="fast"
                    />
                    <p style={{
                        marginTop: '20px',
                        fontSize: '16px',
                        color: '#666',
                        fontWeight: '500'
                    }}>
                        Loading plants...
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="veggie-selector-container">
            {/* Header */}
            <div className="header-row">
                <div className="search-bar">
                    <input
                        type="text"
                        placeholder="Search for plants"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
                <div className="category-buttons">
                    {categories.map((cat) => (
                        <button
                            key={cat}
                            className={cat === selectedCategory ? 'active' : ''}
                            onClick={() => {
                                setSelectedCategory(cat);
                                setSearchTerm('');
                            }}
                        >
                            {cat}
                        </button>
                    ))}
                </div>
            </div>

            {/* Category Label and Progress */}
            <div className="category-title">
                <h2>{searchTerm ? `Search Results` : selectedCategory}</h2>
                <div className="planting-progress-and-button">
                    <div className="progress-bar">
                        <span>{totalPlants}/{Math.round(totalSpace / 4)} Plants Selected</span>
                    </div>
                    <button className="add-button" onClick={handleAddingPlants}>Add to Garden</button>
                </div>
            </div>

            {/* Vertical Scrollable Grid */}
            <div className="veggie-grid-container">
                <div className="veggie-grid">
                    {displayedVeggies.map((veggie) => {
                        const imageLoaded = loadedImages.has(veggie.plant_name);
                        const imageFailed = failedImages.has(veggie.plant_name);

                        return (
                            <div key={veggie.plant_name} className="veggie-card">
                                <div className="info-button" onClick={() => setInfoPopup(veggie)}>i</div>
                                <div className="veggie-image-circle" style={{ position: 'relative' }}>
                                    {/* Show loader while image is loading */}
                                    {!imageLoaded && (
                                        <div style={{
                                            position: 'absolute',
                                            top: '50%',
                                            left: '50%',
                                            transform: 'translate(-50%, -50%)',
                                            zIndex: 2
                                        }}>
                                            <LeafLoader
                                                count={7}
                                                size={16}
                                                speed="fast"
                                                radius={20}
                                            />
                                        </div>
                                    )}

                                    {/* Image - display fallback if original failed */}
                                    <img
                                        src={imageFailed ? fallbackImage : veggie.plant_image_url}
                                        alt={veggie.plant_name}
                                        className="veggie-image"
                                        style={{
                                            opacity: imageLoaded ? 1 : 0,
                                            transition: 'opacity 0.3s ease-in-out'
                                        }}
                                        onLoad={() => handleImageLoad(veggie.plant_name)}
                                        onError={() => handleImageError(veggie.plant_name)}
                                        loading="lazy"
                                    />
                                </div>
                                <h3>{veggie.plant_name}</h3>
                                <div className="counter">
                                    <button onClick={() => handleCountChange(veggie.plant_name, -1)}>-</button>
                                    <span>{plantCounts[veggie.plant_name] || 0}</span>
                                    <button onClick={() => handleCountChange(veggie.plant_name, 1)}>+</button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Info Popup */}
            <VeggieInfoPopup veggie={infoPopup} gardenInfo={gardenInformation} onClose={() => setInfoPopup(null)} />
        </div>
    );
};

export default VeggieSelector;